import React, {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
  PropsWithChildren,
} from "react";
import { MoveFocusInside } from "react-focus-lock";

import { Placement } from "@popperjs/core";

import { StartMenu } from "./StartMenu";
import { StyledMenu, StyledMenuFooter } from "./Styles";
import { Submenu } from "./Submenu";
import { Button } from "../Button";
import { CounterButton } from "../CounterButton";
import { FilterIcon } from "../Icons";
import { IOptionGroupProps, IOptionItemProps, Select } from "../Select";
import { ResizeTransition, SlideLeftRightTransition } from "../Transitions";

export type DataType = {
  id: number | string;
  title: string;
  searchPlaceholder?: string;
  disableInternalSort?: boolean;
  submenu: Array<IOptionGroupProps | IOptionItemProps>;
  isSingleSelect?: boolean;
  defaultValue?: string;
  emptyValue?: string;
  emptyAction?: (e: string | undefined) => void;
};

export type SelectedType = {
  [key: number | string]: Array<string>;
};

export interface FilterProps {
  /** Data rendered in submenus */
  data: Array<DataType>;
  /** Selected data inside submenus */
  selected?: SelectedType;
  /** Main filter label */
  label: string;
  /** Filter width */
  width?: number;
  /** Text displayed for the tooltip of clear all button */
  clearAllText: string;
  /** Handler for changes on the filter */
  onChange: (selected: SelectedType) => void;
  /** Label for reset button inside submenu */
  resetLabel?: string;
  /** Text to show when search results are empty */
  noResultText: string;
  /** Text to show when there are no submenus to display */
  emptyFilterText?: string;
  /** Position of the dropped menu. */
  position?: Placement;
  /** Icon of the trigger element. */
  icon?: ReactElement;
  /** Disabled state of the trigger. */
  disabled?: boolean;
  /** Applies passed classes for label text */
  className?: string;
}

export const Filter: FC<PropsWithChildren<FilterProps>> = ({
  data,
  selected = {},
  label,
  clearAllText,
  resetLabel = "Reset",
  onChange,
  width = 260,
  noResultText,
  emptyFilterText,
  children,
  icon = <FilterIcon />,
  position = "bottom-end",
  disabled = false,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState<number | string>("index");
  const [isLeft, setIsLeft] = useState(true);
  const [height, setHeight] = useState(0);
  const [enteredMenu, setEnteredMenu] = useState<number | string>("index");
  const [filter, setFilter] = useState<SelectedType>(() => selected);

  useEffect(() => {
    if (Object.keys(selected).length > 0 || Object.keys(filter).length > 0) {
      setFilter(selected);
    }
  }, [selected, filter]);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setMenu("index");
    setHeight(0);
  }, []);

  const handleHeight = useCallback((element) => {
    setHeight(element?.offsetHeight ?? 0);
  }, []);

  const handleItemClick = useCallback((value) => {
    setMenu(value);
    setIsLeft(true);
  }, []);

  const setIndex = useCallback(() => {
    setMenu("index");
    setIsLeft(false);
  }, []);

  const handleChange = useCallback(
    (selectedArray) => {
      let newFilters = {};
      if (selectedArray.length) {
        newFilters = {
          ...filter,
          [menu]: selectedArray,
        };
      } else {
        newFilters = { ...filter };
        delete newFilters[menu];
      }
      setFilter(newFilters);
      onChange(newFilters);
    },
    [filter, menu, onChange]
  );

  const handleClearAll = useCallback(
    (event) => {
      event.stopPropagation();
      setFilter({});
      onChange({});
    },
    [onChange]
  );

  const handleReset = useCallback(() => {
    const newFilters = { ...filter };
    delete newFilters[menu];
    setFilter(newFilters);
    onChange(newFilters);
  }, [filter, menu, onChange]);

  const count = useMemo(() => {
    let c = 0;

    Object.values(filter).forEach((entry) => {
      if (typeof entry === "string") {
        return (c += 1);
      }

      return (c += entry.length);
    });
    return c;
  }, [filter]);

  const shouldRenderSubmenus = useMemo(() => {
    if (data.length === 0) {
      return false;
    }

    for (const entry of data) {
      if (entry.submenu?.length > 1) {
        return true;
      }
    }

    return false;
  }, [data]);

  return (
    <StyledMenu
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      position={position}
      target={
        <CounterButton
          active={open}
          label={label}
          icon={icon}
          counter={count}
          tooltipText={clearAllText}
          onClearAll={handleClearAll}
          disabled={disabled}
          labelClassName={className}
        />
      }
    >
      <ResizeTransition in>
        <div style={{ height, width }}>
          <SlideLeftRightTransition
            in={menu === "index"}
            direction={isLeft ? "left" : "right"}
            onEnter={handleHeight}
            onEntered={() => setEnteredMenu("index")}
          >
            <MoveFocusInside>
              <StartMenu
                data={data}
                selected={filter}
                style={{ width }}
                onHeightChange={setHeight}
                onItemClick={handleItemClick}
                shouldRenderSubmenus={shouldRenderSubmenus}
                emptyFilterText={emptyFilterText}
              >
                {children}
              </StartMenu>
            </MoveFocusInside>
          </SlideLeftRightTransition>
          {shouldRenderSubmenus &&
            data.map(
              (
                {
                  id,
                  title,
                  submenu,
                  searchPlaceholder,
                  disableInternalSort,
                  isSingleSelect,
                  defaultValue,
                  ...rest
                }: DataType,
                index: number
              ) => (
                <SlideLeftRightTransition
                  key={`filter-submenu-${index}`}
                  in={menu === id}
                  direction={isLeft ? "left" : "right"}
                  onEnter={handleHeight}
                  onEntered={() => setEnteredMenu(id)}
                >
                  <Submenu
                    title={title}
                    onBack={setIndex}
                    onHeightChange={setHeight}
                    style={{ width }}
                    indexPage={menu === "index"}
                  >
                    <MoveFocusInside disabled={enteredMenu !== id}>
                      <Select
                        options={submenu}
                        selected={filter[id]}
                        onChange={handleChange}
                        type={isSingleSelect ? "single" : "multiple"}
                        autoHeightMax={255}
                        placeholder={searchPlaceholder}
                        noResultText={noResultText}
                        keepSameOptionsOrder={Boolean(submenu.length <= 7)}
                        disableSearch={Boolean(submenu.length <= 7)}
                        disabledInternalSort={disableInternalSort}
                        defaultValue={defaultValue}
                        {...rest}
                      />
                      {filter[id]?.length ? (
                        <StyledMenuFooter>
                          <Button
                            variant="text colored"
                            size="small"
                            onClick={handleReset}
                          >
                            {resetLabel}
                          </Button>
                        </StyledMenuFooter>
                      ) : null}
                    </MoveFocusInside>
                  </Submenu>
                </SlideLeftRightTransition>
              )
            )}
        </div>
      </ResizeTransition>
    </StyledMenu>
  );
};
